import { Box, Text } from 'theme-ui';
import React, { useState, useEffect } from 'react';
import ImgLoader from '../ImgLoader';
import LiteCarousel from '../LiteCarousel';
import MuxVideoPlayer from '../MuxPlayer';

type Props = {
  type: string | null;
  images: Queries.CoverFeatureFragmentFragment['coverImages'];
  layeredImages: Queries.CoverFeatureFragmentFragment['layeredImages'];
  backgroundColor: string | null;
  carouselAutoplaySpeed: number | null;
  showCarouselControls: boolean | null;
  muxVideo: Queries.muxVideoFragmentFragment | null;
  layeredImageTitleText: string | null;
  layeredImageTextOverlap: boolean | null;
  layeredTextOrder: number | null;
};

const CoverMedia = ({
  type,
  images,
  layeredImages,
  backgroundColor,
  carouselAutoplaySpeed,
  showCarouselControls,
  muxVideo,
  layeredImageTitleText,
  layeredImageTextOverlap,
  layeredTextOrder,
}: Props) => {
  if (type === 'Mux Video') {
    return <MuxVideoPlayer muxVideo={muxVideo} forwardSx={{ '--controls': 'none' }} loop={true} autoPlay={true} />;
  }
  if (type === 'Images' || type === 'Carousel' || type === 'Layered Images') {
    // return single static image (if one image) or break off and return correct image feature
    if (!images) return null;

    if (type === 'Layered Images') {
      return (
        <LayeredImages
          images={layeredImages}
          layeredImageTitleText={layeredImageTitleText}
          layeredImageTextOverlap={layeredImageTextOverlap}
          layeredTextOrder={layeredTextOrder}
        />
      );
    }

    if (images.length === 1) {
      return (
        <ImgLoader
          image={images[0]}
          forwardSx={{
            height: '100%',
            width: '100%',
            objectFit: 'cover',
            zIndex: 1,
            backgroundColor: backgroundColor ?? 'white',
          }}
        />
      );
    }

    // if image length > 1
    if (type === 'Images') {
      // choose random image and return it
      return <RandomImage images={images} backgroundColor={backgroundColor} />;
    }
    if (type === 'Carousel') {
      // return carousel
      return (
        <CarouselFeature
          images={images}
          carouselAutoplaySpeed={carouselAutoplaySpeed}
          showCarouselControls={showCarouselControls}
          backgroundColor={backgroundColor}
        />
      );
    }
  }
  return null;
};
type CarouselFeatureProps = {
  images: Queries.CoverFeatureFragmentFragment['coverImages'];
  carouselAutoplaySpeed: number | null;
  backgroundColor: string | null;
  showCarouselControls: boolean | null;
};
const CarouselFeature = ({
  images,
  carouselAutoplaySpeed,
  backgroundColor,
  showCarouselControls,
}: CarouselFeatureProps) => {
  // If showCarouselControls is not explicitly false, show controls
  const showControls = typeof showCarouselControls === 'boolean' ? showCarouselControls : true;
  if (!images) return null;
  return (
    <LiteCarousel
      autoplaySpeed={carouselAutoplaySpeed ? carouselAutoplaySpeed * 1000 : 3000}
      arrows={showControls}
      dots={showControls}
      slides={images.map((image) => (
        <ImgLoader
          key={image?.title}
          image={image}
          forwardSx={{
            height: '100%',
            width: '100%',
            backgroundColor: backgroundColor ?? 'white',
          }}
        />
      ))}
    />
  );
};

type RandomImageProps = {
  images: Queries.CoverFeatureFragmentFragment['coverImages'];
  backgroundColor: string | null;
};
const RandomImage = ({ images, backgroundColor }: RandomImageProps) => {
  const [randomIndex, setRandomIndex] = useState(0);

  useEffect(() => {
    setRandomIndex(getRandomInt(images?.length ?? 0));
  }, [images]);

  const getRandomInt = (max: number) => {
    // return number between 0 and max(not inclusive)
    return Math.floor(Math.random() * max);
  };

  return (
    <ImgLoader
      image={images?.[randomIndex]}
      forwardSx={{
        objectFit: 'cover',
        filter: 'darker',
        height: '100%',
        zIndex: 1,
        backgroundColor: backgroundColor ?? 'white',
      }}
    />
  );
};

type LayeredImagesProps = {
  images: Queries.CoverFeatureFragmentFragment['layeredImages'];
  layeredImageTitleText: string | null;
  layeredImageTextOverlap: boolean | null;
  layeredTextOrder: number | null;
};
const LayeredImages = ({
  images,
  layeredImageTitleText,
  layeredImageTextOverlap,
  layeredTextOrder,
}: LayeredImagesProps) => {
  if (!images) return null;
  // insert index into image array in the correct place
  const index = Math.min(Math.max(layeredTextOrder ?? 0, 0), images.length);
  const items = [...images.slice(0, index), layeredImageTitleText, ...images.slice(index)];

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        position: 'relative',
        isolation: 'isolate',
        overflow: 'hidden',
        transform: 'translateZ(0)', // leave this please, safari quirk...
        maxWidth: '100%',
        '& > div > a > :where(h3, div[data-gatsby-image-wrapper])': {
          transition: 'transform 0.3s ease-in-out',
        },
        '&:hover > div > a > div[data-gatsby-image-wrapper]': {
          transform: 'scale(1)',
        },
        '&:hover > div > a > h3': {
          transform: 'scale(1)',
        },
      }}
    >
      {layeredImageTextOverlap && (
        <Text
          className="layered-text"
          sx={{
            fontFamily: 'body',
            fontSize: ['10rem', 'clamp(20rem,23vw,38rem)'],
            top: ['40%', null, '50%'],
            left: [null, '1.2rem', '1.6rem'],
            position: 'absolute',
            zIndex: 2,
            fontWeight: 'medium',
            letterSpacing: '-.04em',
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
            WebkitTextFillColor: 'transparent',
            WebkitTextStrokeWidth: '2px',
            WebkitTextStrokeColor: 'white',
            color: 'white',
            transformOrigin: 'left',
            transform: 'scale(.95)',
            transition: 'transform 0.3s ease-in-out',
          }}
        >
          {layeredImageTitleText}
        </Text>
      )}
      {items.map((item, index) => {
        // return text or image
        if (typeof item === 'string')
          return (
            <Text
              className="layered-text"
              key={layeredImageTitleText}
              sx={{
                fontFamily: 'body',
                fontSize: ['10rem', 'clamp(20rem,23vw,38rem)'],
                top: ['40%', null, '50%'],
                left: [null, '1.2rem', '1.6rem'],
                position: 'absolute',
                fontWeight: 'medium',
                letterSpacing: '-.04em',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                color: 'white',
                transform: 'scale(.95)',
                transformOrigin: 'left',
                transition: 'transform 0.3s ease-in-out',
              }}
            >
              {layeredImageTitleText}
            </Text>
          );
        if (typeof item === 'object' && item?.id)
          return (
            <Box
              className="layered-images"
              key={item.id}
              sx={{
                top: 0,
                right: 0,
                position: 'absolute',
                height: '100%',
                transition: 'transform 0.3s ease-in-out',
                pointerEvents: 'none',
                transform: `scale(${1 + index * 0.02})`,
                transformOrigin: 'right',
              }}
            >
              <ImgLoader forwardSx={{ height: '100%' }} image={item} alt={item.title} />
            </Box>
          );
        return null;
      })}
    </Box>
  );
};

export default CoverMedia;
